import {
  ANIMATION_LABELS,
  ANIMATION_PRESETS,
  type AnimationPreset,
  type AudioClip,
  audioClipEnd,
  clipDuration,
  DEFAULT_TRANSITION_MS,
  formatTimecode,
  LOOP_ANIMATIONS,
  type LoopAnimation,
  layoutClips,
  moveOverlayClip,
  OUT_ANIMATION_LABELS,
  OVERLAY_LAYOUT_LABELS,
  type OverlayClip,
  type OverlayLayout,
  overlayClipEnd,
  overlayLayoutsForFrame,
  type SceneElement,
  setElementTime,
  stepTrack,
  type TimeRange,
  TRANSCRIPT_LANGUAGE_LABELS,
  TRANSCRIPT_LANGUAGES,
  TRANSITION_KINDS,
  TRANSITION_LABELS,
  type TranscriptInfo,
  type TranscriptLanguage,
  type TransitionKind,
  trackCount,
  VIDEO_FPS,
  videoDuration,
} from "@cva/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  Copy,
  Crop,
  Film,
  FlipHorizontal2,
  Layers,
  Loader2,
  RefreshCw,
  RotateCcw,
  Scissors,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Progress } from "../../components/ui/card";
import { Select } from "../../components/ui/input";
import { estimateProgress, formatClock, formatRemaining, useElapsed } from "../../lib/activity";
import { orpc } from "../../lib/api";
import { errorMessage } from "../../lib/utils";
import { CutoutControl } from "../CutoutControl";
import { useEnv } from "../env";
import { useEditor, useEditorApi } from "../store";
import { IconButton, NumberField, Row, Section, Segmented, Slider, Toggle } from "../ui";
import { ColorSection } from "./ColorSection";
import {
  applyOverlayLayout,
  applyTransition,
  deleteSelectedClip,
  duplicateSelectedClip,
  moveSelectedToMain,
  moveSelectedToTrack,
  overlayBoxShape,
  splitAtPlayhead,
} from "./commands";
import { DeviceSection } from "./DeviceSection";
import { useCaptions } from "./data";
import { OverlayLayoutPicture } from "./LayoutPictures";
import { PersonCutoutSection } from "./PersonCutoutSection";
import { hasPersonCutout, isBehindCutout, setBehindCutout } from "./person-cutout";
import { useVideo, useVideoApi } from "./store";

const seconds = (ms: number) => Math.round(ms / 100) / 10;
const percent = (value: number) => `${Math.round(value * 100)} %`;

const LOOP_LABELS: Record<LoopAnimation, string> = {
  pulse: "Pulsieren",
  float: "Schweben",
  wiggle: "Wackeln",
};

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span className="w-24 shrink-0 text-xs text-zinc-500">{children}</span>;
}

// ---------------------------------------------------------------------------
// Transcript status of a video (clip properties and the captions inspector)
// ---------------------------------------------------------------------------

/** Running recognition: progress between whisper's coarse steps, running and remaining time. */
function TranscriptProgress({ info }: { info: TranscriptInfo }) {
  const elapsed = useElapsed(info.elapsedMs);
  const { progress, remainingMs } = estimateProgress(info.progress, elapsed, info.estimatedMs);
  const details = [
    elapsed !== null && elapsed > 3000 ? `läuft seit ${formatClock(elapsed)}` : null,
    remainingMs === null
      ? null
      : remainingMs < -15_000
        ? "dauert länger als erwartet"
        : formatRemaining(Math.max(0, remainingMs)),
  ].filter(Boolean);
  return (
    <span className="block space-y-1">
      <span className="inline-flex items-center gap-1 text-zinc-600">
        <Loader2 className="size-3 animate-spin" /> Untertitel werden erkannt …{" "}
        {Math.floor(progress)} %
      </span>
      <Progress className="h-1.5" value={progress} />
      {details.length > 0 ? (
        <span className="block text-[11px] text-zinc-500">{details.join(" · ")}</span>
      ) : null}
    </span>
  );
}

export function TranscriptStatus({ assetId, compact }: { assetId: string; compact?: boolean }) {
  const env = useEnv();
  const { infos } = useCaptions();
  const queryClient = useQueryClient();
  const info = infos.get(assetId);
  const asset = env.assets.get(assetId);
  const transcribe = useMutation(
    orpc.media.transcribe.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: orpc.media.transcript.key() }),
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );
  if (asset?.media && !asset.media.hasAudio) {
    return <p className="text-xs text-zinc-500">Dieses Video hat keinen Ton.</p>;
  }
  const language = (info?.language ?? "de") as TranscriptLanguage;
  const start = (next: TranscriptLanguage) =>
    transcribe.mutate({ orgId: env.orgId, assetId, language: next });
  let status: React.ReactNode;
  if (info === undefined) status = <span className="text-zinc-400">Lädt …</span>;
  else if (info === null)
    status =
      asset?.media?.status === "processing" ? (
        <span className="text-zinc-500">Video wird vorbereitet …</span>
      ) : (
        <span className="text-zinc-500">Noch nicht erkannt</span>
      );
  else if (info.status === "queued")
    status = (
      <span className="inline-flex items-center gap-1 text-zinc-600">
        <Loader2 className="size-3 animate-spin" />
        {info.queuePosition
          ? `In der Warteschlange (${info.queuePosition} ${info.queuePosition === 1 ? "Auftrag" : "Aufträge"} vor dir)`
          : "Wartet auf den Start"}
      </span>
    );
  else if (info.status === "processing") status = <TranscriptProgress info={info} />;
  else if (info.status === "failed") status = <span className="text-red-600">{info.error}</span>;
  else
    status = (
      <span className="text-emerald-700">
        {info.words.length} Wörter erkannt
        {info.detectedLanguage
          ? ` · ${TRANSCRIPT_LANGUAGE_LABELS[info.detectedLanguage as TranscriptLanguage] ?? info.detectedLanguage.toUpperCase()}`
          : ""}
      </span>
    );
  return (
    <div className="space-y-2">
      <div className="text-xs">{status}</div>
      {!compact || info?.status === "failed" || info === null ? (
        <Row>
          <Select
            className="h-8 flex-1"
            value={language}
            disabled={!env.canEdit}
            onChange={(event) => start(event.target.value as TranscriptLanguage)}
            title="Sprache der Aufnahme"
          >
            {TRANSCRIPT_LANGUAGES.map((entry) => (
              <option key={entry} value={entry}>
                {TRANSCRIPT_LANGUAGE_LABELS[entry]}
              </option>
            ))}
          </Select>
          <IconButton
            label={info ? "Neu erkennen" : "Sprache erkennen"}
            disabled={!env.canEdit || transcribe.isPending}
            onClick={() => start(language)}
          >
            <RefreshCw />
          </IconButton>
        </Row>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main track clip
// ---------------------------------------------------------------------------

export function ClipProperties() {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const env = useEnv();
  const selected = useVideo((s) => s.selectedClip);
  const framingMode = useVideo((s) => s.framing);
  const doc = useEditor((s) => s.doc);
  if (!selected) return null;
  if (selected.track === "audio") return <AudioClipProperties id={selected.id} />;
  if (selected.track === "overlay") return <OverlayClipProperties id={selected.id} />;
  const entry = layoutClips(doc.timeline).find((item) => item.clip.id === selected.id);
  if (!entry) return null;
  const { clip } = entry;
  const asset = env.assets.get(clip.assetId);
  const update = (recipe: (target: typeof clip) => void, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.timeline?.clips.find((item) => item.id === clip.id);
        if (target) recipe(target);
      },
      merge ? { merge: `clip-${clip.id}-${merge}` } : undefined,
    );
  const isVideo = clip.kind === "video";
  return (
    <div>
      <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-2.5">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-900">{isVideo ? "Videoclip" : "Bild"}</p>
          <p className="truncate text-xs text-zinc-500">{clip.name ?? asset?.name}</p>
        </div>
        <div className="flex items-center">
          <IconButton
            label="Teilen am Abspielkopf"
            shortcut="S"
            onClick={() => splitAtPlayhead(store, video)}
          >
            <Scissors />
          </IconButton>
          <IconButton label="Duplizieren" onClick={() => duplicateSelectedClip(store, video)}>
            <Copy />
          </IconButton>
          <IconButton label="Löschen" shortcut="⌫" onClick={() => deleteSelectedClip(store, video)}>
            <Trash2 />
          </IconButton>
        </div>
      </div>
      <div className="border-b border-zinc-100 px-4 py-2.5">
        <button
          type="button"
          onClick={() => moveSelectedToTrack(store, video)}
          className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50 [&_svg]:size-4"
          title="Der Clip liegt dann als Overlay über dem Bild (B-Roll, Bild im Bild); die Hauptspur schließt die Lücke."
        >
          <Layers /> Auf eigene Spur legen (Overlay)
        </button>
        {isVideo ? null : (
          <CutoutControl
            className="mt-2"
            target={{ kind: "clip", id: clip.id }}
            assetId={clip.assetId}
          />
        )}
      </div>
      <PersonCutoutSection clip={clip} clipRef={{ track: "video", id: clip.id }} />
      <ColorSection clip={clip} track="video" />
      {clip.box ? (
        <Section
          title="Bildbereich"
          actions={
            <button
              type="button"
              onClick={() =>
                update((target) => {
                  delete target.box;
                  delete target.radius;
                })
              }
              className="text-xs font-medium text-brand-700 hover:underline"
              title="Der Clip füllt wieder das ganze Bild"
            >
              Vollbild
            </button>
          }
        >
          <p className="text-xs text-zinc-500">
            Teil einer Bildaufteilung (Bausteine › Layouts). Auf der Fläche verschieben und an den
            Ecken größer ziehen.
          </p>
          <Row>
            <NumberField
              label="X"
              value={Math.round(clip.box.x)}
              onChange={(value) =>
                update((target) => {
                  if (target.box) target.box.x = value;
                }, "box-x")
              }
            />
            <NumberField
              label="Y"
              value={Math.round(clip.box.y)}
              onChange={(value) =>
                update((target) => {
                  if (target.box) target.box.y = value;
                }, "box-y")
              }
            />
          </Row>
          <Row>
            <NumberField
              label="B"
              value={Math.round(clip.box.width)}
              min={10}
              onChange={(value) =>
                update((target) => {
                  if (target.box) target.box.width = value;
                }, "box-w")
              }
            />
            <NumberField
              label="H"
              value={Math.round(clip.box.height)}
              min={10}
              onChange={(value) =>
                update((target) => {
                  if (target.box) target.box.height = value;
                }, "box-h")
              }
            />
          </Row>
          <Row>
            <FieldLabel>Ecken</FieldLabel>
            <Slider
              value={clip.radius ?? 0}
              min={0}
              max={Math.round(Math.min(clip.box.width, clip.box.height) / 2)}
              step={1}
              onChange={(value) => update((target) => (target.radius = value), "radius")}
            />
            <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
              {Math.round(clip.radius ?? 0)} px
            </span>
          </Row>
        </Section>
      ) : null}
      <Section title="Länge">
        <p className="text-xs text-zinc-500">
          {formatTimecode(entry.start)} – {formatTimecode(entry.end)} auf der Zeitleiste ·{" "}
          {formatTimecode(entry.end - entry.start)}
        </p>
        {isVideo ? (
          <>
            <Row>
              <NumberField
                label="Von"
                unit="s"
                precision={1}
                step={0.1}
                value={seconds(clip.sourceIn)}
                min={0}
                max={seconds(clip.sourceOut) - 0.1}
                onChange={(value) =>
                  update((target) => (target.sourceIn = Math.round(value * 1000)), "in")
                }
              />
              <NumberField
                label="Bis"
                unit="s"
                precision={1}
                step={0.1}
                value={seconds(clip.sourceOut)}
                min={seconds(clip.sourceIn) + 0.1}
                max={asset?.durationMs ? seconds(asset.durationMs) : undefined}
                onChange={(value) =>
                  update((target) => (target.sourceOut = Math.round(value * 1000)), "out")
                }
              />
            </Row>
            <div className="space-y-1">
              <span className="text-xs text-zinc-500">Tempo</span>
              <Segmented
                className="w-full"
                value={String(clip.speed)}
                onChange={(value) => update((target) => (target.speed = Number(value)))}
                options={[0.5, 0.75, 1, 1.5, 2, 3].map((speed) => ({
                  value: String(speed),
                  label: `${speed.toLocaleString("de-DE")}×`,
                  title:
                    speed === 1
                      ? "Normal"
                      : `${speed.toLocaleString("de-DE")}-fache Geschwindigkeit`,
                }))}
              />
            </div>
          </>
        ) : (
          <NumberField
            label="Dauer"
            unit="s"
            precision={1}
            step={0.5}
            value={seconds(clip.sourceOut - clip.sourceIn)}
            min={0.2}
            max={600}
            onChange={(value) =>
              update(
                (target) => (target.sourceOut = target.sourceIn + Math.round(value * 1000)),
                "duration",
              )
            }
          />
        )}
      </Section>
      {isVideo ? (
        <Section title="Ton">
          <Row>
            <FieldLabel>Lautstärke</FieldLabel>
            <Slider
              value={clip.volume}
              min={0}
              max={2}
              step={0.05}
              onChange={(value) => update((target) => (target.volume = value), "volume")}
            />
            <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
              {percent(clip.volume)}
            </span>
          </Row>
          <Toggle
            checked={clip.muted}
            onChange={(muted) => update((target) => (target.muted = muted))}
            label="Stumm"
            description="Ohne Ton, auch keine Untertitel für diesen Clip"
          />
        </Section>
      ) : null}
      <Section
        title="Bildausschnitt"
        actions={
          <IconButton
            label="Zurücksetzen"
            onClick={() =>
              update((target) => {
                target.framing = { fit: "cover", zoom: 1, x: 0, y: 0, flipX: false };
              })
            }
          >
            <RotateCcw />
          </IconButton>
        }
      >
        <Segmented
          className="w-full"
          value={clip.framing.fit}
          onChange={(fit) => update((target) => (target.framing.fit = fit))}
          options={[
            {
              value: "cover",
              label: "Füllen",
              title: "Füllt das ganze Bild (schneidet Ränder ab)",
            },
            { value: "contain", label: "Einpassen", title: "Ganzes Bild mit Rand" },
          ]}
        />
        <Row>
          <FieldLabel>Zoom</FieldLabel>
          <Slider
            value={clip.framing.zoom}
            min={0.5}
            max={3}
            step={0.01}
            onChange={(value) => update((target) => (target.framing.zoom = value), "zoom")}
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {percent(clip.framing.zoom)}
          </span>
        </Row>
        <Row>
          <FieldLabel>Horizontal</FieldLabel>
          <Slider
            value={clip.framing.x}
            min={-1}
            max={1}
            step={0.01}
            onChange={(value) => update((target) => (target.framing.x = value), "x")}
          />
        </Row>
        <Row>
          <FieldLabel>Vertikal</FieldLabel>
          <Slider
            value={clip.framing.y}
            min={-1}
            max={1}
            step={0.01}
            onChange={(value) => update((target) => (target.framing.y = value), "y")}
          />
        </Row>
        <Row>
          <button
            type="button"
            onClick={() => video.getState().setFraming(!framingMode)}
            className={`inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border text-xs font-medium [&_svg]:size-4 ${framingMode ? "border-brand-500 bg-brand-50 text-brand-700" : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}
          >
            <Crop /> Auf der Fläche verschieben
          </button>
          <IconButton
            label="Spiegeln"
            active={clip.framing.flipX}
            onClick={() => update((target) => (target.framing.flipX = !target.framing.flipX))}
          >
            <FlipHorizontal2 />
          </IconButton>
        </Row>
        <div className="space-y-1">
          <span className="text-xs text-zinc-500">Rand</span>
          <Segmented
            className="w-full"
            value={clip.background}
            onChange={(background) => update((target) => (target.background = background))}
            options={[
              { value: "color", label: "Hintergrundfarbe", title: "Farbe der Seite" },
              { value: "blur", label: "Unscharf", title: "Unscharfe Kopie des Videos" },
            ]}
          />
        </div>
      </Section>
      {entry.index > 0 ? (
        <Section title="Übergang vom Clip davor">
          <Select
            value={clip.transition?.kind ?? ""}
            onChange={(event) => {
              const kind = event.target.value as TransitionKind | "";
              applyTransition(
                store,
                clip.id,
                kind
                  ? { kind, duration: clip.transition?.duration ?? DEFAULT_TRANSITION_MS }
                  : null,
              );
            }}
          >
            <option value="">Harter Schnitt</option>
            {TRANSITION_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {TRANSITION_LABELS[kind]}
              </option>
            ))}
          </Select>
          {clip.transition ? (
            <Row>
              <FieldLabel>Dauer</FieldLabel>
              <Slider
                value={clip.transition.duration}
                min={100}
                max={2000}
                step={50}
                onChange={(value) =>
                  applyTransition(
                    store,
                    clip.id,
                    { kind: clip.transition!.kind, duration: value },
                    "duration",
                  )
                }
              />
              <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
                {seconds(entry.overlap)} s
              </span>
            </Row>
          ) : null}
        </Section>
      ) : null}
      <Section title="Blenden (Schwarz)">
        <Row>
          <FieldLabel>Einblenden</FieldLabel>
          <Slider
            value={clip.fadeIn}
            min={0}
            max={2000}
            step={50}
            onChange={(value) => update((target) => (target.fadeIn = value), "fade-in")}
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {seconds(clip.fadeIn)} s
          </span>
        </Row>
        <Row>
          <FieldLabel>Ausblenden</FieldLabel>
          <Slider
            value={clip.fadeOut}
            min={0}
            max={2000}
            step={50}
            onChange={(value) => update((target) => (target.fadeOut = value), "fade-out")}
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {seconds(clip.fadeOut)} s
          </span>
        </Row>
      </Section>
      {isVideo ? (
        <Section title="Spracherkennung">
          <TranscriptStatus assetId={clip.assetId} />
        </Section>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Clip on a video track above the main track
// ---------------------------------------------------------------------------

function OverlayClipProperties({ id }: { id: string }) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const env = useEnv();
  const framingMode = useVideo((s) => s.framing);
  const doc = useEditor((s) => s.doc);
  const clip = doc.timeline?.overlays?.find((item) => item.id === id);
  if (!clip) return null;
  const asset = env.assets.get(clip.assetId);
  const source = { width: asset?.width ?? 1920, height: asset?.height ?? 1080 };
  const isVideo = clip.kind === "video";
  const tracks = trackCount(doc);
  const update = (recipe: (target: OverlayClip) => void, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.timeline?.overlays?.find((item) => item.id === id);
        if (target) recipe(target);
      },
      merge ? { merge: `overlay-${id}-${merge}` } : undefined,
    );
  /** Changes of time or length: when the clip then overlaps a neighbour, it moves up a track. */
  const updateTiming = (recipe: (target: OverlayClip) => void, merge: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.timeline?.overlays?.find((item) => item.id === id);
        if (!target) return;
        recipe(target);
        moveOverlayClip(draft, id, target.start, target.track);
      },
      { merge: `overlay-${id}-${merge}` },
    );
  const layout = (value: OverlayLayout) => applyOverlayLayout(store, id, value, source);
  const moveTrack = (delta: 1 | -1) =>
    store.getState().update((draft) => {
      stepTrack(draft, id, delta);
    });
  const box = clip.box;
  const round = (value: number) => Math.round(value);
  return (
    <div>
      <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-2.5">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-900">
            {isVideo ? "Video" : "Bild"} auf Spur {clip.track + 1}
          </p>
          <p className="truncate text-xs text-zinc-500">{clip.name ?? asset?.name}</p>
        </div>
        <div className="flex items-center">
          <IconButton
            label="Teilen am Abspielkopf"
            shortcut="S"
            onClick={() => splitAtPlayhead(store, video)}
          >
            <Scissors />
          </IconButton>
          <IconButton label="Duplizieren" onClick={() => duplicateSelectedClip(store, video)}>
            <Copy />
          </IconButton>
          <IconButton label="Löschen" shortcut="⌫" onClick={() => deleteSelectedClip(store, video)}>
            <Trash2 />
          </IconButton>
        </div>
      </div>
      {isVideo ? null : (
        <div className="border-b border-zinc-100 px-4 py-2.5">
          <CutoutControl target={{ kind: "overlay", id: clip.id }} assetId={clip.assetId} />
        </div>
      )}
      {clip.device ? null : (
        <PersonCutoutSection clip={clip} clipRef={{ track: "overlay", id: clip.id }} />
      )}
      <DeviceSection clip={clip} asset={asset} />
      <ColorSection clip={clip} track="overlay" />
      <Section title="Anordnung">
        <div className="grid grid-cols-4 gap-1">
          {overlayLayoutsForFrame(doc.format).map((value) => (
            <LayoutButton
              key={value}
              layout={value}
              frame={doc.format}
              source={overlayBoxShape(clip, source)}
              onClick={() => layout(value)}
            />
          ))}
        </div>
        <Row>
          <NumberField
            label="X"
            value={round(box.x)}
            onChange={(value) => update((target) => (target.box.x = value), "x")}
          />
          <NumberField
            label="Y"
            value={round(box.y)}
            onChange={(value) => update((target) => (target.box.y = value), "y")}
          />
        </Row>
        <Row>
          <NumberField
            label="B"
            value={round(box.width)}
            min={10}
            onChange={(value) => update((target) => (target.box.width = value), "w")}
          />
          <NumberField
            label="H"
            value={round(box.height)}
            min={10}
            onChange={(value) => update((target) => (target.box.height = value), "h")}
          />
        </Row>
        {clip.device ? null : (
          <Row>
            <FieldLabel>Ecken</FieldLabel>
            <Slider
              value={clip.radius}
              min={0}
              max={Math.round(Math.min(box.width, box.height) / 2)}
              step={1}
              onChange={(value) => update((target) => (target.radius = value), "radius")}
            />
            <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
              {Math.round(clip.radius)} px
            </span>
          </Row>
        )}
        <Row>
          <FieldLabel>Deckkraft</FieldLabel>
          <Slider
            value={clip.opacity}
            min={0.05}
            max={1}
            step={0.01}
            onChange={(value) => update((target) => (target.opacity = value), "opacity")}
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {percent(clip.opacity)}
          </span>
        </Row>
        <button
          type="button"
          onClick={() => moveSelectedToMain(store, video)}
          className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50 [&_svg]:size-4"
          title="Fügt den Clip am Abspielkopf in die Hauptspur ein"
        >
          <Film /> In die Hauptspur verschieben
        </button>
        <Row>
          <FieldLabel>Ebene</FieldLabel>
          <IconButton label="Eine Spur nach oben" onClick={() => moveTrack(1)}>
            <ArrowUp />
          </IconButton>
          <IconButton
            label="Eine Spur nach unten"
            disabled={clip.track <= 1}
            onClick={() => moveTrack(-1)}
          >
            <ArrowDown />
          </IconButton>
          <span className="text-xs text-zinc-500">
            Spur {clip.track + 1} von {tracks + 1}
          </span>
        </Row>
        {hasPersonCutout(doc) && !clip.cutout ? (
          <Toggle
            checked={isBehindCutout(doc, clip.id)}
            onChange={(behind) => setBehindCutout(store, [clip.id], behind)}
            label="Hinter die Person"
            description="Das Bild oder Video liegt zwischen Hintergrund und freigestellter Person – z. B. als neuer Hintergrund."
          />
        ) : null}
      </Section>
      <Section title="Zeit">
        <Row>
          <NumberField
            label="Start"
            unit="s"
            precision={1}
            step={0.1}
            value={seconds(clip.start)}
            min={0}
            onChange={(value) =>
              updateTiming(
                (target) => (target.start = Math.max(0, Math.round(value * 1000))),
                "start",
              )
            }
          />
          <NumberField
            label="Länge"
            unit="s"
            precision={1}
            step={0.1}
            value={seconds(clipDuration(clip))}
            min={0.2}
            max={
              isVideo && asset?.durationMs
                ? seconds((asset.durationMs - clip.sourceIn) / clip.speed)
                : 600
            }
            onChange={(value) =>
              updateTiming(
                (target) =>
                  (target.sourceOut = target.sourceIn + Math.round(value * 1000 * target.speed)),
                "length",
              )
            }
          />
        </Row>
        {isVideo ? (
          <>
            <Row>
              <NumberField
                label="Ab"
                unit="s"
                precision={1}
                step={0.1}
                value={seconds(clip.sourceIn)}
                min={0}
                max={seconds(clip.sourceOut) - 0.1}
                onChange={(value) =>
                  update((target) => (target.sourceIn = Math.round(value * 1000)), "in")
                }
              />
              <span className="self-center text-xs text-zinc-500">Stelle im Video</span>
            </Row>
            <div className="space-y-1">
              <span className="text-xs text-zinc-500">Tempo</span>
              <Segmented
                className="w-full"
                value={String(clip.speed)}
                onChange={(value) =>
                  updateTiming((target) => (target.speed = Number(value)), "speed")
                }
                options={[0.5, 1, 1.5, 2].map((speed) => ({
                  value: String(speed),
                  label: `${speed.toLocaleString("de-DE")}×`,
                  title:
                    speed === 1
                      ? "Normal"
                      : `${speed.toLocaleString("de-DE")}-fache Geschwindigkeit`,
                }))}
              />
            </div>
          </>
        ) : null}
        <p className="text-xs text-zinc-500">
          {formatTimecode(clip.start)} – {formatTimecode(overlayClipEnd(clip))} auf der Zeitleiste
        </p>
      </Section>
      <Section
        title="Bildausschnitt"
        actions={
          <IconButton
            label="Zurücksetzen"
            onClick={() =>
              update((target) => {
                target.framing = { fit: "cover", zoom: 1, x: 0, y: 0, flipX: false };
              })
            }
          >
            <RotateCcw />
          </IconButton>
        }
      >
        <Segmented
          className="w-full"
          value={clip.framing.fit}
          onChange={(fit) => update((target) => (target.framing.fit = fit))}
          options={[
            { value: "cover", label: "Füllen", title: "Füllt den Rahmen (schneidet Ränder ab)" },
            { value: "contain", label: "Einpassen", title: "Ganzes Bild im Rahmen" },
          ]}
        />
        <Row>
          <FieldLabel>Zoom</FieldLabel>
          <Slider
            value={clip.framing.zoom}
            min={0.5}
            max={3}
            step={0.01}
            onChange={(value) => update((target) => (target.framing.zoom = value), "zoom")}
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {percent(clip.framing.zoom)}
          </span>
        </Row>
        <Row>
          <button
            type="button"
            onClick={() => video.getState().setFraming(!framingMode)}
            className={`inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border text-xs font-medium [&_svg]:size-4 ${framingMode ? "border-brand-500 bg-brand-50 text-brand-700" : "border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}
          >
            <Crop /> Bild im Rahmen verschieben
          </button>
          <IconButton
            label="Spiegeln"
            active={clip.framing.flipX}
            onClick={() => update((target) => (target.framing.flipX = !target.framing.flipX))}
          >
            <FlipHorizontal2 />
          </IconButton>
        </Row>
      </Section>
      {isVideo ? (
        <Section title="Ton">
          <Row>
            <FieldLabel>Lautstärke</FieldLabel>
            <Slider
              value={clip.volume}
              min={0}
              max={2}
              step={0.05}
              onChange={(value) => update((target) => (target.volume = value), "volume")}
            />
            <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
              {percent(clip.volume)}
            </span>
          </Row>
          <Toggle
            checked={clip.muted}
            onChange={(muted) => update((target) => (target.muted = muted))}
            label="Stumm"
            description="Typisch für B-Roll: nur die Stimme der Hauptspur ist zu hören"
          />
        </Section>
      ) : null}
      <Section title="Ein- und Ausblenden">
        <Row>
          <FieldLabel>Einblenden</FieldLabel>
          <Slider
            value={clip.fadeIn}
            min={0}
            max={2000}
            step={50}
            onChange={(value) => update((target) => (target.fadeIn = value), "fade-in")}
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {seconds(clip.fadeIn)} s
          </span>
        </Row>
        <Row>
          <FieldLabel>Ausblenden</FieldLabel>
          <Slider
            value={clip.fadeOut}
            min={0}
            max={2000}
            step={50}
            onChange={(value) => update((target) => (target.fadeOut = value), "fade-out")}
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {seconds(clip.fadeOut)} s
          </span>
        </Row>
      </Section>
    </div>
  );
}

/** Button with a small picture of a layout preset: the frame with the box in it. */
function LayoutButton({
  layout,
  frame,
  source,
  onClick,
}: {
  layout: OverlayLayout;
  frame: { width: number; height: number };
  source: { width: number; height: number };
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={OVERLAY_LAYOUT_LABELS[layout]}
      className="flex flex-col items-center gap-0.5 rounded-md border border-zinc-200 py-1 hover:border-brand-400 hover:bg-brand-50"
    >
      <OverlayLayoutPicture layout={layout} frame={frame} source={source} />
      <span className="text-[9px] leading-tight text-zinc-500">
        {OVERLAY_LAYOUT_LABELS[layout]}
      </span>
    </button>
  );
}

function AudioClipProperties({ id }: { id: string }) {
  const store = useEditorApi();
  const video = useVideoApi()!;
  const env = useEnv();
  const doc = useEditor((s) => s.doc);
  const clip = doc.timeline?.audio.find((item) => item.id === id);
  if (!clip) return null;
  const asset = env.assets.get(clip.assetId);
  const update = (recipe: (target: AudioClip) => void, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.timeline?.audio.find((item) => item.id === id);
        if (target) recipe(target);
      },
      merge ? { merge: `audio-${id}-${merge}` } : undefined,
    );
  return (
    <div>
      <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-2.5">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-900">Musik</p>
          <p className="truncate text-xs text-zinc-500">{clip.name ?? asset?.name}</p>
        </div>
        <div className="flex items-center">
          <IconButton
            label="Teilen am Abspielkopf"
            shortcut="S"
            onClick={() => splitAtPlayhead(store, video)}
          >
            <Scissors />
          </IconButton>
          <IconButton label="Löschen" shortcut="⌫" onClick={() => deleteSelectedClip(store, video)}>
            <Trash2 />
          </IconButton>
        </div>
      </div>
      <Section title="Position">
        <Row>
          <NumberField
            label="Start"
            unit="s"
            precision={1}
            step={0.1}
            value={seconds(clip.start)}
            min={0}
            onChange={(value) =>
              update((target) => (target.start = Math.round(value * 1000)), "start")
            }
          />
          <NumberField
            label="Länge"
            unit="s"
            precision={1}
            step={0.1}
            value={seconds(clip.sourceOut - clip.sourceIn)}
            min={0.2}
            max={asset?.durationMs ? seconds(asset.durationMs - clip.sourceIn) : undefined}
            onChange={(value) =>
              update(
                (target) => (target.sourceOut = target.sourceIn + Math.round(value * 1000)),
                "length",
              )
            }
          />
        </Row>
        <p className="text-xs text-zinc-500">
          Endet bei {formatTimecode(audioClipEnd(clip))}
          {audioClipEnd(clip) > videoDuration(doc)
            ? " – nach dem Video, wird am Ende ausgeblendet"
            : ""}
        </p>
      </Section>
      <Section title="Lautstärke">
        <Row>
          <FieldLabel>Lautstärke</FieldLabel>
          <Slider
            value={clip.volume}
            min={0}
            max={2}
            step={0.05}
            onChange={(value) => update((target) => (target.volume = value), "volume")}
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {percent(clip.volume)}
          </span>
        </Row>
        <Toggle
          checked={clip.ducking}
          onChange={(ducking) => update((target) => (target.ducking = ducking))}
          label="Leiser, wenn gesprochen wird"
          description="Senkt die Musik automatisch unter die Stimme (nach der Spracherkennung)."
        />
        <Row>
          <FieldLabel>Einblenden</FieldLabel>
          <Slider
            value={clip.fadeIn}
            min={0}
            max={5000}
            step={100}
            onChange={(value) => update((target) => (target.fadeIn = value), "fade-in")}
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {seconds(clip.fadeIn)} s
          </span>
        </Row>
        <Row>
          <FieldLabel>Ausblenden</FieldLabel>
          <Slider
            value={clip.fadeOut}
            min={0}
            max={5000}
            step={100}
            onChange={(value) => update((target) => (target.fadeOut = value), "fade-out")}
          />
          <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
            {seconds(clip.fadeOut)} s
          </span>
        </Row>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overlays: visible time and animation
// ---------------------------------------------------------------------------

export function TimeProperties({ elements }: { elements: SceneElement[] }) {
  const store = useEditorApi();
  const video = useVideoApi();
  const doc = useEditor((s) => s.doc);
  if (!video || elements.length !== 1) return null;
  const element = elements[0]!;
  const duration = videoDuration(doc);
  const range = element.time;
  const update = (recipe: (target: SceneElement) => void, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target = draft.elements[element.id];
        if (target) recipe(target);
      },
      merge ? { merge: `time-${element.id}-${merge}` } : undefined,
    );
  const setStep = (which: "in" | "out", preset: AnimationPreset | "", duration?: number) =>
    update((target) => {
      const animation = { ...target.animation };
      if (!preset) delete animation[which];
      else animation[which] = { preset, duration: duration ?? animation[which]?.duration ?? 500 };
      target.animation = Object.keys(animation).length > 0 ? animation : undefined;
    });
  const playhead = () => Math.round(video.getState().time);
  const group = element.type === "group";
  /** New visible time; a group takes its content along (setElementTime). */
  const setTime = (next: (current: TimeRange) => TimeRange, merge?: string) =>
    store.getState().update(
      (draft) => {
        const current = draft.elements[element.id]?.time;
        if (current) setElementTime(draft, element.id, next(current));
      },
      merge ? { merge: `time-${element.id}-${merge}` } : undefined,
    );
  const topLevel = doc.pages.some((page) => page.children.includes(element.id));
  const behindOption = topLevel && element.type !== "captions" && hasPersonCutout(doc);
  return (
    <>
      {behindOption ? (
        <Section title="Person im Video">
          <Toggle
            checked={isBehindCutout(doc, element.id)}
            onChange={(behind) => setBehindCutout(store, [element.id], behind)}
            label="Hinter die Person"
            description="Liegt auf einer Spur unter der Person-Spur: zwischen Hintergrund und freigestellter Person."
          />
        </Section>
      ) : null}
      <Section title="Sichtbar im Video">
        <Toggle
          checked={!range}
          onChange={(whole) =>
            update((target) => {
              target.time = whole ? undefined : { start: 0, end: duration };
            })
          }
          label="Die ganze Zeit"
        />
        {range ? (
          <>
            <Row>
              <NumberField
                label="Von"
                unit="s"
                precision={1}
                step={0.1}
                min={0}
                value={seconds(range.start)}
                onChange={(value) => {
                  const start = Math.round(value * 1000);
                  setTime((time) => ({ start, end: Math.max(start + 100, time.end) }), "start");
                }}
              />
              <NumberField
                label="Bis"
                unit="s"
                precision={1}
                step={0.1}
                min={0.1}
                value={seconds(range.end)}
                onChange={(value) => {
                  const end = Math.round(value * 1000);
                  setTime((time) => ({ start: Math.min(time.start, end - 100), end }), "end");
                }}
              />
            </Row>
            <Row>
              <button
                type="button"
                className="h-7 flex-1 rounded-md border border-zinc-200 text-xs text-zinc-700 hover:bg-zinc-50"
                title={
                  group
                    ? "Verschiebt die ganze Gruppe mit ihren Animationen an den Abspielkopf"
                    : undefined
                }
                onClick={() => {
                  const start = playhead();
                  // A group (building block) moves as a whole, so its animations stay intact.
                  setTime((time) =>
                    group
                      ? { start, end: start + time.end - time.start }
                      : { start, end: Math.max(start + 100, time.end) },
                  );
                }}
              >
                {group ? "An den Abspielkopf" : "Ab Abspielkopf"}
              </button>
              <button
                type="button"
                className="h-7 flex-1 rounded-md border border-zinc-200 text-xs text-zinc-700 hover:bg-zinc-50"
                onClick={() => {
                  const end = playhead();
                  setTime((time) => ({ start: Math.min(time.start, end - 100), end }));
                }}
              >
                Bis Abspielkopf
              </button>
            </Row>
          </>
        ) : null}
      </Section>
      <Section title="Animation">
        {(["in", "out"] as const).map((which) => {
          const step = element.animation?.[which];
          const labels = which === "in" ? ANIMATION_LABELS : OUT_ANIMATION_LABELS;
          const presets = ANIMATION_PRESETS.filter(
            (preset) => preset !== "typewriter" || (element.type === "text" && which === "in"),
          );
          return (
            <div key={which} className="space-y-1.5">
              <Row>
                <FieldLabel>{which === "in" ? "Erscheinen" : "Verschwinden"}</FieldLabel>
                <Select
                  className="h-8 flex-1"
                  value={step?.preset ?? ""}
                  onChange={(event) => setStep(which, event.target.value as AnimationPreset | "")}
                >
                  <option value="">Ohne</option>
                  {presets.map((preset) => (
                    <option key={preset} value={preset}>
                      {labels[preset]}
                    </option>
                  ))}
                </Select>
              </Row>
              {step ? (
                <Row>
                  <FieldLabel>Dauer</FieldLabel>
                  <Slider
                    value={step.duration}
                    min={100}
                    max={3000}
                    step={50}
                    onChange={(value) =>
                      store.getState().update(
                        (draft) => {
                          const target = draft.elements[element.id]?.animation?.[which];
                          if (target) target.duration = value;
                        },
                        { merge: `anim-${element.id}-${which}` },
                      )
                    }
                  />
                  <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums">
                    {seconds(step.duration)} s
                  </span>
                </Row>
              ) : null}
            </div>
          );
        })}
        <Row>
          <FieldLabel>Dauerhaft</FieldLabel>
          <Select
            className="h-8 flex-1"
            value={element.animation?.loop ?? ""}
            onChange={(event) =>
              update((target) => {
                const animation = { ...target.animation };
                const loop = event.target.value as LoopAnimation | "";
                if (loop) animation.loop = loop;
                else delete animation.loop;
                target.animation = Object.keys(animation).length > 0 ? animation : undefined;
              })
            }
          >
            <option value="">Ohne</option>
            {LOOP_ANIMATIONS.map((loop) => (
              <option key={loop} value={loop}>
                {LOOP_LABELS[loop]}
              </option>
            ))}
          </Select>
        </Row>
        <button
          type="button"
          className="text-xs font-medium text-brand-700 hover:underline"
          onClick={() => {
            const start = element.time?.start ?? 0;
            video.getState().seek(start);
            video.getState().play();
          }}
        >
          Vorschau abspielen
        </button>
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Whole video (nothing selected)
// ---------------------------------------------------------------------------

export function VideoSettings() {
  const store = useEditorApi();
  const doc = useEditor((s) => s.doc);
  const fps = doc.timeline?.fps ?? 30;
  return (
    <Section title="Video">
      <p className="text-xs text-zinc-500">
        {doc.format.width} × {doc.format.height} px · {formatTimecode(videoDuration(doc))} ·{" "}
        {doc.timeline?.clips.length ?? 0} Clips
      </p>
      <Row>
        <FieldLabel>Bildrate</FieldLabel>
        <Segmented
          value={String(fps)}
          onChange={(value) =>
            store.getState().update((draft) => {
              if (draft.timeline) draft.timeline.fps = Number(value);
            })
          }
          options={VIDEO_FPS.map((value) => ({
            value: String(value),
            label: String(value),
            title: `${value} Bilder pro Sekunde`,
          }))}
        />
      </Row>
    </Section>
  );
}
